import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Phone, Mail, Plus, Eye } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useMyProfile } from '@/hooks/profile';
import { useUserRoleName } from '@/hooks/permissions';
import { useDashboardLeaveData, useDashboardTimesheetData } from '@/hooks/dashboard';
import { formatDistanceToNow, format } from 'date-fns';
import { getAvatarUrl } from '@/utils/avatarUtils';
import { useNavigate } from 'react-router-dom';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';

export const ProfileCard = () => {
  const navigate = useNavigate();
  const { data: profile, isLoading: profileLoading } = useMyProfile();
  const { roleName, isLoading: roleLoading } = useUserRoleName();
  const { data: leaveData, isLoading: leaveLoading } = useDashboardLeaveData(profile?.id || '');
  const { data: timesheetData, isLoading: timesheetLoading } = useDashboardTimesheetData(profile?.id || '');

  if (profileLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return null;
  }

  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown User';
  const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase();

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'rejected':
        return 'destructive';
      case 'in-progress':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const formatTimeRange = (clockIn: string, clockOut: string | null) => {
    const startTime = format(new Date(clockIn), 'h:mma');
    if (clockOut) {
      const endTime = format(new Date(clockOut), 'h:mma');
      return `${startTime} - ${endTime}`;
    }
    return `${startTime} -`;
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = format(new Date(startDate), 'MMM dd');
    const end = format(new Date(endDate), 'MMM dd');
    return start === end ? start : `${start} - ${end}`;
  };

  const calculateTotalHours = (clockIn: string, clockOut: string | null) => {
    if (!clockOut) return 'In progress';
    
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    
    const hours = Math.floor(diffHours);
    const minutes = Math.round((diffHours - hours) * 60);
    
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-6">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Avatar className="w-16 h-16">
              <AvatarImage 
                src={getAvatarUrl(profile.avatar_url, profile.company_id)} 
                alt={fullName}
              />
              <AvatarFallback className="text-lg font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h3 className="text-xl font-semibold">{fullName}</h3>
              <p className="text-sm text-muted-foreground capitalize">{roleLoading ? '...' : (roleName || 'No Role')}</p>
            </div>
          </div>
          <div className="flex flex-col space-y-2 text-sm text-muted-foreground">
            {profile.date_of_birth ? (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(profile.date_of_birth), 'dd MMM yyyy')}</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span className="text-blue-600 cursor-pointer">Set your date of birth</span>
              </div>
            )}
            {profile.mobile_phone ? (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>{profile.mobile_phone}</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span className="text-blue-600 cursor-pointer">Set your mobile</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span className="break-all">{profile.email}</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Leave Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold">Leave</h4>
            <Button 
              size="sm" 
              onClick={() => navigate('/leave')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add new
            </Button>
          </div>

          {/* Leave Balances Table */}
          {leaveLoading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Desktop table header */}
              <div className="hidden sm:grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                <span>Leave type</span>
                <span>Current Balance</span>
                <span>Pending</span>
                <span>Remaining</span>
              </div>
              {leaveData?.leaveBalances.map((balance) => {
                const remaining = balance.current_balance_days - balance.pending_days;
                return (
                  <div key={balance.id}>
                    {/* Desktop row */}
                    <div className="hidden sm:grid grid-cols-4 gap-4 text-sm">
                      <span className="font-medium">{getLeaveTypeLabel(balance.policy.leave_type)}</span>
                      <span>{balance.current_balance_days} days</span>
                      <span>{balance.pending_days} days</span>
                      <span>{remaining} days</span>
                    </div>
                    {/* Mobile card */}
                    <div className="sm:hidden border rounded-lg p-3 space-y-1">
                      <p className="font-medium">{getLeaveTypeLabel(balance.policy.leave_type)}</p>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Balance: {balance.current_balance_days}d</span>
                        <span>Pending: {balance.pending_days}d</span>
                        <span>Left: {remaining}d</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Most Recent Leave Request */}
          {leaveData?.recentRequest && (
            <div className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(leaveData.recentRequest.start_date), 'EEE dd MMM yyyy')}
                    </span>
                    <Badge variant={getStatusBadgeVariant(leaveData.recentRequest.status)}>
                      {leaveData.recentRequest.status.toUpperCase()}
                    </Badge>
                  </div>
                  <p className="font-medium">Leave Request</p>
                  <p className="text-sm text-muted-foreground">
                    {leaveData.recentRequest.leave_type} • {formatDateRange(leaveData.recentRequest.start_date, leaveData.recentRequest.end_date)}
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/leave')}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Recent Time Sheets Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold">Recent time sheets</h4>
            <Button 
              size="sm" 
              onClick={() => navigate('/timesheets')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add new
            </Button>
          </div>

          {/* Most Recent Timesheet */}
          {timesheetLoading ? (
            <div className="animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ) : timesheetData?.recentEntry ? (
            <div className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(timesheetData.recentEntry.created_at), 'EEE dd MMM')}
                    </span>
                    <Badge variant={getStatusBadgeVariant(timesheetData.recentEntry.status)}>
                      {timesheetData.recentEntry.status.toUpperCase()}
                    </Badge>
                  </div>
                  <p className="font-medium">Time Entry</p>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {formatTimeRange(timesheetData.recentEntry.clock_in, timesheetData.recentEntry.clock_out)}
                    </p>
                    <p className="text-sm font-medium text-green-600">
                      Total: {calculateTotalHours(timesheetData.recentEntry.clock_in, timesheetData.recentEntry.clock_out)}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/timesheets')}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-8 h-8 mx-auto mb-2" />
              <p>No recent timesheets</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
